import { useState } from 'react';
import { debug } from '@/lib/debug';
import type { LeadMetaEvent } from '@/lib/gtm';
// import { trackFormSubmission } from './metaPixel';

// ==================== VALIDATION FUNCTIONS ====================

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[\w-\.]+@([\w-]+\.)+[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validates phone number format
 */
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[\d\s-()]{7,20}$/;
  return phoneRegex.test(phone);
};

/**
 * Validates that a field is not empty
 */
export const validateRequired = (value: string): boolean => {
  return value.trim() !== '';
};

// ==================== FIELD CHANGE HANDLERS ====================

/**
 * Creates an email change handler with validation
 */
export const createEmailChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  setIsInvalid: React.Dispatch<React.SetStateAction<boolean>>,
  fieldName: string = 'Email'
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData((prev: any) => ({ ...prev, [fieldName]: email }));
    
    if (email.trim() !== '') {
      setIsInvalid(!validateEmail(email.trim()));
    } else {
      setIsInvalid(false);
    }
  };
};

/**
 * Creates a phone change handler with validation
 */
export const createPhoneChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  setIsInvalid: React.Dispatch<React.SetStateAction<boolean>>,
  fieldName: string = 'Phone_number'
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setFormData((prev: any) => ({ ...prev, [fieldName]: phone }));
    
    if (phone.trim() !== '') {
      setIsInvalid(!validatePhone(phone.trim()));
    } else {
      setIsInvalid(false);
    }
  };
};

/**
 * Creates a required field change handler with validation
 */
export const createRequiredFieldChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  setIsInvalid: React.Dispatch<React.SetStateAction<boolean>>,
  fieldName: string
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev: any) => ({ ...prev, [fieldName]: value }));
    
    if (value.trim() !== '') {
      setIsInvalid(false);
    } else {
      setIsInvalid(true);
    }
  };
};

/**
 * Creates a simple field change handler without validation
 */
export const createSimpleFieldChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  fieldName: string
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev: any) => ({ ...prev, [fieldName]: value }));
  };
};

/**
 * Creates a textarea change handler with character limit
 */
export const createTextAreaChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  setIsInvalid: React.Dispatch<React.SetStateAction<boolean>>,
  fieldName: string,
  currentValue: string,
  maxLength: number = 500
) => {
  return (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const remainingChars = maxLength - currentValue.length;

    if (newText.length <= maxLength) {
      setFormData((prev: any) => ({ ...prev, [fieldName]: newText }));
      if (newText.length < maxLength) {
        setIsInvalid(false);
      }
    } else {
      // If we're adding text and there's room for some of it
      if (newText.length > currentValue.length && remainingChars > 0) {
        // Take only the chars that fit within the limit
        const partialText = newText.slice(0, currentValue.length + remainingChars);
        setFormData((prev: any) => ({ ...prev, [fieldName]: partialText }));
      }
      setIsInvalid(true);
      setTimeout(() => {
        setIsInvalid(false);
      }, 2000);
    }
  };
};

/**
 * Creates a dropdown change handler
 */
export const createDropdownChangeHandler = (
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  fieldName: string
) => {
  return (value: string) => {
    setFormData((prev: any) => ({ ...prev, [fieldName]: value }));
  };
};

// ==================== VALIDATION HINTS ====================

export const VALIDATION_HINTS = {
  EMAIL_INVALID: 'Please enter a valid email address',
  PHONE_INVALID: 'Please enter a valid phone number',
  FIRST_NAME_REQUIRED: 'First name is required',
  LAST_NAME_REQUIRED: 'Last name is required',
  NAME_REQUIRED: 'Name is required',
  FIELD_REQUIRED: 'Please complete this field first.',
  DROPDOWN_REQUIRED: 'Please select an option to proceed.',
  DESIRED_SERVICE_REQUIRED: 'Please specify your desired service',
  CHARACTER_LIMIT_REACHED: 'Maximum character limit reached (500 characters)',
  DROPDOWN_CLEAR: 'Clear',
  DUPLICATE_EMAIL: 'The emails cannot be the same',
  DUPLICATE_PHONE: 'The phones cannot be the same',
};

export const VALIDATION_HINTS_AR: {
  EMAIL_INVALID: string;
  PHONE_INVALID: string;
  FIRST_NAME_REQUIRED: string;
  LAST_NAME_REQUIRED: string;
  NAME_REQUIRED: string;
  FIELD_REQUIRED: string;
  DROPDOWN_REQUIRED: string;
  DESIRED_SERVICE_REQUIRED: string;
  CHARACTER_LIMIT_REACHED: string;
  DROPDOWN_CLEAR: string;
  DUPLICATE_EMAIL: string;
  DUPLICATE_PHONE: string;
} = {
  EMAIL_INVALID: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
  PHONE_INVALID: 'يرجى إدخال رقم هاتف صحيح',
  FIRST_NAME_REQUIRED: 'الاسم الأول مطلوب',
  LAST_NAME_REQUIRED: 'اسم العائلة مطلوب',
  NAME_REQUIRED: 'الاسم مطلوب',
  FIELD_REQUIRED: 'يرجى تعبئة هذا الحقل اولاً.',
  DROPDOWN_REQUIRED: 'يرجى اختيار احد الخيارات للمتابعة.',
  DESIRED_SERVICE_REQUIRED: 'يرجى تحديد الخدمة المطلوبة',
  CHARACTER_LIMIT_REACHED: 'تم الوصول إلى الحد الأقصى لعدد الأحرف (500 حرف)',
  DROPDOWN_CLEAR: 'مسح',
  DUPLICATE_EMAIL: 'لا يمكن أن يتطابق البريدان',
  DUPLICATE_PHONE: 'لا يمكن أن يتطابق الرقمان',
};

// ==================== FORM SUBMISSION ====================

/**
 * Submits form data to Zoho CRM
 */
export const submitToZoho = async (
  formData: any,
  systemSource?: string,
  /** The Meta event this submission counts as; `/api/zoho-submit` sends its server copy. */
  metaEvent?: { event: LeadMetaEvent; eventId: string },
): Promise<{ success: boolean; error?: string; leadToken?: string | null }> => {
  try {
    const dataToSubmit = systemSource
      ? { ...formData, System_Source: systemSource }
      : formData;

    const response = await fetch('/api/zoho-submit', {
      method: 'POST',
      body: JSON.stringify({
        data: dataToSubmit,
        metaEvent,
      })
    });

    if (!response.ok) {
      throw new Error('Failed to submit form');
    }

    const result = await response.json().catch(() => ({} as { leadToken?: string | null }));

    debug.log('Form submitted successfully');

    // Track form submission with Meta Pixel for manual matching
    // commented out cause we are setting up stape
    // trackFormSubmission(dataToSubmit, 'Lead');

    return { success: true, leadToken: result?.leadToken ?? null };
  } catch (error) {
    console.error('Error submitting form:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// ==================== FORM VALIDATION CHECKS ====================

/**
 * Checks if all mandatory fields in the form have values
 */
export const checkAllMandatoryFields = (formData: any, mandatoryFields: string[]): boolean => {
  return mandatoryFields.every(field => formData[field]?.trim() !== '');
};

/**
 * Checks if form has any validation errors
 */
export const checkFormValidationErrors = (
  validationStates: Record<string, boolean>,
  hasAllMandatoryFields: boolean
): boolean => {
  const hasAnyInvalidField = Object.values(validationStates).some(isInvalid => isInvalid);
  return hasAnyInvalidField || !hasAllMandatoryFields;
};

// ==================== CUSTOM HOOK FOR FORM STATE ====================

/**
 * Custom hook for managing form state with validation
 */
export const useFormState = <T extends Record<string, any>>(initialState: T) => {
  const [formData, setFormData] = useState<T>(initialState);
  const [validationStates, setValidationStates] = useState<Record<string, boolean>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const setFieldValid = (fieldName: string, isValid: boolean) => {
    setValidationStates(prev => ({ ...prev, [fieldName]: !isValid }));
  };

  const resetForm = () => {
    setFormData(initialState);
    setValidationStates({});
    setSubmitStatus('idle');
  };

  return {
    formData,
    setFormData,
    validationStates,
    setFieldValid,
    submitStatus,
    setSubmitStatus,
    resetForm,
  };
};

